import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Bell, FileText, Award, MessageCircle, Calendar,
    FileSignature, CheckCheck, X, Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem('surya_token');
const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

const ICON_MAP = {
    'file-text': FileText,
    'award': Award,
    'message-circle': MessageCircle,
    'calendar': Calendar,
    'file-signature': FileSignature,
    'bell': Bell,
};

const COLOR_MAP = {
    'application_status': '#1E3A8A',
    'subsidy_credited': '#16A34A',
    'quotation': '#0891B2',
    'complaint_reply': '#7C3AED',
    'complaint_status': '#DC2626',
    'site_survey': '#EA580C',
};

/**
 * NotificationsBell
 * ─────────────────────────────────────────────────────────────
 * Bell icon with a live unread badge that opens a right-side sheet
 * showing an aggregated feed of recent events for the logged-in
 * customer (application status, quotes, subsidy, complaints, surveys).
 *
 * Auto-polls every 60s while mounted so the badge stays reasonably
 * fresh without user interaction.
 */
export default function NotificationsBell() {
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({ items: [], unread_count: 0 });
    const [loading, setLoading] = useState(false);

    const load = async () => {
        if (!token()) return;
        try {
            const { data } = await axios.get(`${API}/customer/notifications`, { headers: authHeaders() });
            setData(data);
        } catch {
            /* silent — the bell should never crash the header */
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 60_000);
        return () => clearInterval(t);
    }, []);

    const openSheet = async () => {
        setOpen(true);
        setLoading(true);
        await load();
        setLoading(false);
    };

    const markAllRead = async () => {
        try {
            await axios.post(`${API}/customer/notifications/mark-read`, {}, { headers: authHeaders() });
            setData(d => ({
                ...d,
                unread_count: 0,
                items: (d.items || []).map(i => ({ ...i, read: true })),
            }));
        } catch { /* ignore */ }
    };

    const markOneRead = async (id) => {
        // Optimistic update — flip `read` locally right away, decrement badge.
        setData(d => {
            const items = (d.items || []).map(i => i.id === id ? { ...i, read: true } : i);
            const unread = items.filter(i => !i.read).length;
            return { ...d, items, unread_count: unread };
        });
        try {
            await axios.post(`${API}/customer/notifications/${encodeURIComponent(id)}/read`, {}, { headers: authHeaders() });
        } catch {
            /* if it fails, the next 60s poll will restore the correct state */
        }
    };

    const openLink = (item) => {
        if (item && !item.read) markOneRead(item.id);
        if (item?.link) nav(item.link);
        setOpen(false);
    };

    return (
        <>
            <button
                onClick={openSheet}
                className='relative w-9 h-9 grid place-items-center rounded-lg hover:bg-muted'
                data-testid='customer-notifications'
                aria-label='Notifications'
            >
                <Bell className='w-4 h-4' />
                {data.unread_count > 0 && (
                    <span
                        className='absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center'
                        data-testid='notifications-unread-badge'
                    >
                        {data.unread_count > 99 ? '99+' : data.unread_count}
                    </span>
                )}
            </button>

            {open && (
                <>
                    {/* Backdrop */}
                    <div
                        className='fixed inset-0 z-50 bg-black/40 backdrop-blur-sm animate-in fade-in duration-150'
                        onClick={() => setOpen(false)}
                        aria-hidden
                    />
                    {/* Sheet */}
                    <aside
                        className='fixed top-0 right-0 z-50 w-full max-w-sm h-full bg-background shadow-2xl flex flex-col animate-in slide-in-from-right duration-200'
                        data-testid='notifications-sheet'
                    >
                        <header className='px-4 py-3 border-b flex items-center justify-between'>
                            <div>
                                <h2 className='font-display font-bold text-[16px] leading-tight'>Notifications</h2>
                                <div className='text-[11px] text-muted-foreground bilingual-sub'>
                                    सूचनाएँ • {data.items.length} recent
                                </div>
                            </div>
                            <div className='flex items-center gap-2'>
                                {data.unread_count > 0 && (
                                    <button
                                        onClick={markAllRead}
                                        className='inline-flex items-center gap-1 text-[11.5px] text-primary hover:underline'
                                        data-testid='notifications-mark-read'
                                    >
                                        <CheckCheck className='w-3.5 h-3.5' /> Mark all read
                                    </button>
                                )}
                                <button
                                    onClick={() => setOpen(false)}
                                    className='w-8 h-8 rounded-lg hover:bg-muted grid place-items-center'
                                    aria-label='Close'
                                    data-testid='notifications-close'
                                >
                                    <X className='w-4 h-4' />
                                </button>
                            </div>
                        </header>
                        <div className='flex-1 overflow-y-auto'>
                            {loading && data.items.length === 0 ? (
                                <div className='p-8 text-center text-muted-foreground text-sm'>
                                    <Loader2 className='w-5 h-5 mx-auto animate-spin mb-2' />
                                    Loading…
                                </div>
                            ) : data.items.length === 0 ? (
                                <div className='p-8 text-center'>
                                    <div className='w-14 h-14 mx-auto rounded-2xl grid place-items-center mb-2'
                                        style={{ background: 'rgba(30,58,138,0.08)', color: 'var(--brand-navy, #1E3A8A)' }}>
                                        <Bell className='w-7 h-7' />
                                    </div>
                                    <div className='font-semibold text-[14px]'>You're all caught up</div>
                                    <div className='text-[12px] text-muted-foreground mt-0.5'>
                                        कोई नई सूचना नहीं है।
                                    </div>
                                </div>
                            ) : (
                                <ul className='divide-y'>
                                    {data.items.map(n => {
                                        const Icon = ICON_MAP[n.icon] || Bell;
                                        const color = COLOR_MAP[n.type] || 'var(--brand-navy, #1E3A8A)';
                                        const isUnread = !n.read;
                                        return (
                                            <li key={n.id}>
                                                <button
                                                    onClick={() => openLink(n)}
                                                    className={cn(
                                                        'w-full text-left px-4 py-3 flex gap-3 hover:bg-muted/60 focus:outline-none focus-visible:bg-muted relative',
                                                        isUnread && 'bg-accent/[0.04]',
                                                    )}
                                                    data-testid={`notification-item-${n.type}`}
                                                    data-notif-id={n.id}
                                                    data-read={n.read ? 'true' : 'false'}
                                                >
                                                    {isUnread && (
                                                        <span
                                                            className='absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full'
                                                            style={{ background: 'var(--brand-orange, #F97316)' }}
                                                            aria-label='unread'
                                                        />
                                                    )}
                                                    <div
                                                        className={cn(
                                                            'w-9 h-9 rounded-xl grid place-items-center shrink-0 text-white transition-opacity',
                                                            !isUnread && 'opacity-70',
                                                        )}
                                                        style={{ background: color }}
                                                    >
                                                        <Icon className='w-4.5 h-4.5' />
                                                    </div>
                                                    <div className='flex-1 min-w-0'>
                                                        <div className={cn(
                                                            'text-[13px] leading-snug',
                                                            isUnread ? 'font-bold text-foreground' : 'font-medium text-muted-foreground',
                                                        )}>
                                                            {n.title}
                                                        </div>
                                                        {n.body && (
                                                            <div className={cn(
                                                                'text-[11.5px] mt-0.5 line-clamp-2',
                                                                isUnread ? 'text-foreground/80' : 'text-muted-foreground',
                                                            )}>{n.body}</div>
                                                        )}
                                                        <div className='text-[10.5px] text-muted-foreground/80 mt-1'>
                                                            {relTime(n.timestamp)}
                                                        </div>
                                                    </div>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </aside>
                </>
            )}
        </>
    );
}

// Short "3 min ago" / "2 hr ago" / "yesterday" formatter
function relTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)} hr ago`;
    if (diff < 172800) return 'yesterday';
    if (diff < 604800) return `${Math.round(diff / 86400)} days ago`;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}
